import {
  type CoordinateSystem,
  type Float32Array2D,
  type GridRequest,
  crop,
  intersectWeatherModelBoundingBox,
  isZeroPadding,
  withPadding32f,
} from "@mm/api.meteomatics.com";

export const BINARY_RESERVED_FILL_VALUE = -999;

type Padding = { top: number; left: number; bottom: number; right: number };

export type BinaryCropPlan<C extends CoordinateSystem> =
  | { kind: "empty"; outputWidth: number; outputHeight: number }
  | {
      kind: "request";
      request: GridRequest<C>;
      outputWidth: number;
      outputHeight: number;
      padding?: Padding;
    };

export function prepareBinaryCrop<C extends CoordinateSystem>(request: GridRequest<C>): BinaryCropPlan<C> {
  const outputWidth = request.width;
  const outputHeight = request.height;
  if (!request.boundingBoxLimit) {
    return { kind: "request", request, outputWidth, outputHeight };
  }

  const intersectionModelBounds = intersectWeatherModelBoundingBox(request, request.boundingBoxLimit);
  if (intersectionModelBounds.kind === "OutOfBounds") {
    return { kind: "empty", outputWidth, outputHeight };
  }

  if (intersectionModelBounds.kind === "PartiallyOutOfBounds") {
    const intersection = intersectionModelBounds.intersection.project(request.area.crs);
    const cropped = crop(request, intersection, request.boundingBoxLimit.project(request.area.crs));

    if (isZeroPadding(cropped.padding)) {
      return {
        kind: "request",
        request: cropped.gridRequest,
        outputWidth,
        outputHeight,
      };
    }

    return {
      kind: "request",
      request: cropped.gridRequest,
      outputWidth,
      outputHeight,
      padding: {
        ...cropped.padding,
        // Binary response tile is south-up; invert vertical padding to map into north-up tile space.
        top: cropped.padding.bottom,
        bottom: cropped.padding.top,
      },
    };
  }

  return { kind: "request", request, outputWidth, outputHeight };
}

export function toBinaryEmptyPayload(outputWidth: number, outputHeight: number): Float32Array {
  const payload = new Float32Array(outputWidth * outputHeight);
  payload.fill(BINARY_RESERVED_FILL_VALUE);
  return payload;
}

export function applyBinaryPadding(
  payload: Float32Array,
  inputWidth: number,
  inputHeight: number,
  padding: Padding,
): Float32Array2D {
  return withPadding32f(payload, { width: inputWidth, height: inputHeight }, padding, BINARY_RESERVED_FILL_VALUE);
}
